/**
 * SitesAdmin — Spectropharma multi-site registry (V26).
 *
 * Owner: drafts/55 v0.8 — multi-site eDMS foundation.
 *
 * Three real Spectropharma sites:
 *   - RIYADH-1   — Manufacturing HQ
 *   - JEDDAH-2   — Sterile injectables plant
 *   - DAMMAM-3   — Distribution / WHS
 *
 * Renders the site card grid on top with key metadata, and the site
 * authorization matrix below — which roles can perform which actions at
 * which sites. This is the read view; mutations come later in v0.9.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import { useSites, useSiteAuthMatrix, type Site, type SiteAuthMatrixEntry } from '../../api/orgHooks';
import styles from './SitesAdmin.module.css';

const KIND_LABELS: Record<Site['site_kind'], string> = {
  MANUFACTURING_HQ: 'Manufacturing HQ',
  MANUFACTURING: 'Manufacturing',
  PACKAGING: 'Packaging',
  WAREHOUSE: 'Warehouse',
  LAB_QC: 'QC Laboratory',
  HEAD_OFFICE: 'Head Office',
  DISTRIBUTION: 'Distribution',
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return iso;
  }
}

export function SitesAdmin() {
  const { t } = useTranslation();
  const sitesQ = useSites();
  const matrixQ = useSiteAuthMatrix();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  const sites = sitesQ.data?.items ?? [];
  const matrix = matrixQ.data?.items ?? [];

  const filteredMatrix = useMemo(() => {
    if (!selectedSiteId) return matrix;
    return matrix.filter((m) => m.site_id === selectedSiteId);
  }, [matrix, selectedSiteId]);

  const matrixBySite = useMemo(() => {
    const grouped = new Map<string, SiteAuthMatrixEntry[]>();
    for (const m of filteredMatrix) {
      const arr = grouped.get(m.site_code) ?? [];
      arr.push(m);
      grouped.set(m.site_code, arr);
    }
    return grouped;
  }, [filteredMatrix]);

  return (
    <div className={styles.page}>
      <PageHero
        eyebrow={t('sitesAdmin.eyebrow', { defaultValue: 'Admin' })}
        title={t('sitesAdmin.title', { defaultValue: 'Sites' })}
        subtitle={t('sitesAdmin.subtitle', {
          defaultValue:
            'Spectropharma operates three regulated sites across Saudi Arabia. Each site has its own SFDA license, quality head, authorized person, and per-action authorization matrix. Documents become effective per-site; training is assigned per-site.',
        })}
      />

      <section className={styles.cardGrid}>
        {sites.map((s) => {
          const selected = s.site_id === selectedSiteId;
          return (
            <button
              key={s.site_id}
              type="button"
              className={`${styles.siteCard} ${selected ? styles.siteCardActive : ''}`}
              onClick={() => setSelectedSiteId(selected ? null : s.site_id)}
            >
              <div className={styles.siteCardHead}>
                <span className={styles.siteCode}>{s.site_code}</span>
                <span className={`${styles.statusPill} ${s.is_active ? styles.statusActive : styles.statusInactive}`}>
                  {s.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <h2 className={styles.siteName}>{s.name}</h2>
              <dl className={styles.metaGrid}>
                <div>
                  <dt>City</dt>
                  <dd>
                    {s.city}, {s.country_code}
                  </dd>
                </div>
                <div>
                  <dt>Kind</dt>
                  <dd>{KIND_LABELS[s.site_kind] ?? s.site_kind}</dd>
                </div>
                <div>
                  <dt>GxP scope</dt>
                  <dd>{s.gxp_in_scope ? 'In scope' : 'Out of scope'}</dd>
                </div>
                <div>
                  <dt>License</dt>
                  <dd className={styles.mono}>{s.license_ref ?? '—'}</dd>
                </div>
                <div>
                  <dt>License expiry</dt>
                  <dd>{fmtDate(s.license_expiry)}</dd>
                </div>
                <div>
                  <dt>Timezone</dt>
                  <dd className={styles.mono}>{s.timezone}</dd>
                </div>
              </dl>
              {s.notes && <p className={styles.notes}>{s.notes}</p>}
            </button>
          );
        })}
        {sitesQ.isLoading && <div className={styles.empty}>Loading sites…</div>}
        {sitesQ.isError && <div className={styles.empty}>Failed to load sites.</div>}
        {!sitesQ.isLoading && sites.length === 0 && <div className={styles.empty}>No sites registered yet.</div>}
      </section>

      <section className={styles.matrixSection}>
        <header className={styles.matrixHeader}>
          <h2 className={styles.sectionTitle}>
            {selectedSiteId
              ? `Authorization matrix — ${sites.find((s) => s.site_id === selectedSiteId)?.site_code ?? ''}`
              : 'Site authorization matrix'}
          </h2>
          {selectedSiteId && (
            <button type="button" className={styles.clearBtn} onClick={() => setSelectedSiteId(null)}>
              Show all sites
            </button>
          )}
        </header>
        <p className={styles.subtitle}>
          Which role is required to perform each gated action at each site. Dual-sign actions require a second signer
          in the indicated role.
        </p>

        {[...matrixBySite.entries()].map(([siteCode, entries]) => (
          <div key={siteCode} className={styles.matrixGroup}>
            <h3 className={styles.matrixGroupTitle}>{siteCode}</h3>
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Primary role</th>
                  <th>Dual-sign</th>
                  <th>Secondary role</th>
                  <th>Effective from</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.matrix_id}>
                    <td className={styles.mono}>{e.action_code}</td>
                    <td>{e.required_role}</td>
                    <td>
                      {e.dual_sign_required ? (
                        <span className={styles.dualSignPill}>Required</span>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>{e.secondary_role ?? <span className={styles.muted}>—</span>}</td>
                    <td>{fmtDate(e.effective_from_utc)}</td>
                    <td className={styles.notes}>{e.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {matrixQ.isLoading && <div className={styles.empty}>Loading authorization matrix…</div>}
        {!matrixQ.isLoading && matrix.length === 0 && (
          <div className={styles.empty}>No authorization-matrix rows yet.</div>
        )}
      </section>
    </div>
  );
}
